import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import * as React from 'react';
import type { Notification, NotificationPoll } from '@senviro/core-api-client';
import { useAuth } from '@/auth/AuthProvider';
import { apiFetch } from '@/lib/api-fetch';

/**
 * Filter options for {@link useNotifications}. We keep these aligned with
 * Core's `pollNotifications` filter (`unreadOnly`, `since`) plus a small
 * `limit` cap to bound the page render.
 */
export interface UseNotificationsOptions {
  unreadOnly?: boolean;
  since?: string;
  limit?: number;
  /** When false (e.g. for the bell dropdown), suspend the 30s focus poll. */
  poll?: boolean;
}

export const NOTIFICATIONS_QUERY_KEY = ['notifications'] as const;

export function notificationsQueryKey(opts: UseNotificationsOptions = {}): readonly unknown[] {
  return [...NOTIFICATIONS_QUERY_KEY, opts.unreadOnly ?? null, opts.since ?? null, opts.limit ?? null];
}

/**
 * `useNotifications` (W6-F4-S1).
 *
 * Polls `GET /api/v1/proxy/core/notifications` every 30s while the window
 * has focus. The proxy forwards to Core's `pollNotifications` (W2-F5) — so
 * Senviro never persists its own notification rows.
 */
export function useNotifications(
  opts: UseNotificationsOptions = {},
): UseQueryResult<NotificationPoll> {
  const { getAccessToken } = useAuth();
  const focused = useWindowFocus();
  const shouldPoll = opts.poll !== false && focused;
  const params = new URLSearchParams();
  if (opts.unreadOnly !== undefined) params.set('unreadOnly', String(opts.unreadOnly));
  if (opts.since) params.set('since', opts.since);
  if (opts.limit !== undefined) params.set('limit', String(opts.limit));
  const qs = params.toString() ? `?${params.toString()}` : '';

  return useQuery<NotificationPoll>({
    queryKey: notificationsQueryKey(opts),
    queryFn: () =>
      apiFetch<NotificationPoll>(
        `/api/v1/proxy/core/notifications${qs}`,
        // App-wide 30s background poll — a notifications-side outage must
        // degrade silently (empty list), never fire the global serverError
        // toast that blanks whatever page the user is actually on.
        { getAccessToken, skipServerErrorReport: true },
      ),
    // W120-F1: 25s stale time prevents back-to-back component-mount
    // refetches on route changes while still letting the 30s
    // polling interval do its job (an explicit refetchInterval
    // bypasses staleTime). Caught during 2026-05-24 audit walk: the
    // bell + sidebar + dropdown each independently mounted the
    // hook on every navigation, fan-firing the proxy.
    staleTime: 25_000,
    refetchInterval: shouldPoll ? 30_000 : false,
    refetchOnWindowFocus: shouldPoll,
  });
}

/** Tiny hook tracking `document.hasFocus()` for the polling gate. */
function useWindowFocus(): boolean {
  const [focused, setFocused] = React.useState<boolean>(() =>
    typeof document === 'undefined' ? true : document.hasFocus(),
  );
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const onFocus = () => setFocused(true);
    const onBlur = () => setFocused(false);
    window.addEventListener('focus', onFocus);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('blur', onBlur);
    };
  }, []);
  return focused;
}

/** Convenience selector: returns just the items array. */
export function selectNotifications(poll: NotificationPoll | undefined): Notification[] {
  return poll?.items ?? [];
}
